/**
 * orgChartService - CRUD for organisation chart trees
 * Collection: schools/{schoolId}/hierarchies
 * Single document per org chart with embedded flat position array.
 *
 * Position shape:
 *   { id, label, type: 'role'|'team', refId, parentId, order,
 *     staffCards: [{ uid, displayName, email, photoURL }],
 *     classCards: [{ classId, className }],
 *     gradeCards: [{ gradeId, gradeName }],
 *     subjectCards: [{ subjectId, subjectName }] }
 */

import { addDoc, collection, deleteDoc, doc, getDocs, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { getDescendantIds, getAllPositions } from './organisationScope';

const getPath = (schoolId) => `schools/${schoolId}/hierarchies`;

const orgChartService = {
  async getOrgCharts(schoolId) {
    const snapshot = await getDocs(collection(db, getPath(schoolId)));
    const orgCharts = snapshot.docs.map(item => ({ ...item.data(), id: item.id }));
    return orgCharts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  },

  async createOrgChart(schoolId, { name, createdBy }) {
    const created = await addDoc(collection(db, getPath(schoolId)), {
      name,
      nodes: [],
      createdAt: new Date(),
      createdBy,
      updatedAt: new Date(),
      updatedBy: createdBy,
    });
    return created.id;
  },

  async savePositions(schoolId, orgChartId, positions, updatedBy) {
    // A delayed drag/reorder must not silently revoke or restore an authority
    // assignment made since that drag began. Responsibilities have their own
    // explicit transaction below; layout/card saves preserve the latest ones.
    const ref = doc(db, getPath(schoolId), orgChartId);
    return runTransaction(db, async transaction => {
      const snapshot = await transaction.get(ref);
      if (!snapshot.exists()) throw new Error('This organisation chart is no longer available.');
      const saved = new Map((snapshot.data().nodes || []).map(node => [node.id, node]));
      transaction.update(ref, {
        nodes: positions.map(position => ({ ...position, responsibilities: saved.get(position.id)?.responsibilities || [] })),
        updatedAt: new Date(), updatedBy,
      });
    });
  },

  async setPositionResponsibility(schoolId, orgChartId, positionId, responsibility, enabled, updatedBy) {
    const ref = doc(db, getPath(schoolId), orgChartId);
    return runTransaction(db, async transaction => {
      const snapshot = await transaction.get(ref);
      if (!snapshot.exists() || !snapshot.data().nodes?.some(node => node.id === positionId)) throw new Error('This position is no longer available.');
      const nodes = snapshot.data().nodes.map(position => {
        if (position.id !== positionId) return position;
        const responsibilities = new Set(position.responsibilities || []);
        if (enabled) responsibilities.add(responsibility);
        else responsibilities.delete(responsibility);
        return { ...position, responsibilities: [...responsibilities] };
      });
      transaction.update(ref, { nodes, updatedAt: new Date(), updatedBy });
    });
  },

  async deleteOrgChart(schoolId, orgChartId) {
    return deleteDoc(doc(db, getPath(schoolId), orgChartId));
  },

  /**
   * Convert flat position array to tree structure (for dnd-kit-sortable-tree)
   */
  buildTree(flatPositions, collapsedSet = new Set()) {
    if (!flatPositions || flatPositions.length === 0) return [];

    const positionMap = new Map();
    flatPositions.forEach(pos => positionMap.set(pos.id, { ...pos, children: [] }));

    const roots = [];
    flatPositions.forEach(pos => {
      const treeNode = positionMap.get(pos.id);
      const hasChildren = flatPositions.some(p => p.parentId === pos.id);
      treeNode.hasChildren = hasChildren;
      treeNode.collapsed = collapsedSet.has(pos.id);
      treeNode.value = pos.label;

      if (!pos.parentId) {
        roots.push(treeNode);
      } else {
        const parent = positionMap.get(pos.parentId);
        if (parent) {
          parent.children.push(treeNode);
        } else {
          roots.push(treeNode);
        }
      }
    });

    const sortChildren = (items) => {
      items.sort((a, b) => (a.order || 0) - (b.order || 0));
      items.forEach(n => sortChildren(n.children));
    };
    sortChildren(roots);

    return roots;
  },

  /**
   * Convert tree structure back to flat position array (for Firestore save)
   */
  flattenTree(treeItems, parentId = null) {
    const result = [];
    treeItems.forEach((item, index) => {
      const { children } = item;
      const transient = new Set(['children', 'hasChildren', 'collapsed', 'value']);
      const posData = Object.fromEntries(Object.entries(item).filter(([key]) => !transient.has(key)));
      result.push({
        ...posData,
        parentId,
        order: index + 1,
      });
      if (children && children.length > 0) {
        result.push(...this.flattenTree(children, item.id));
      }
    });
    return result;
  },

  generatePositionId() {
    return `pos_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  },

  addCard(positions, positionId, cardType, card) {
    return positions.map(p => {
      if (p.id !== positionId) return p;
      const existing = p[cardType] || [];
      const key = cardType === 'staffCards' ? 'uid' : cardType === 'classCards' ? 'classId' : cardType === 'gradeCards' ? 'gradeId' : 'subjectId';
      if (existing.some(c => c[key] === card[key])) return p;
      return { ...p, [cardType]: [...existing, card] };
    });
  },

  removeCard(positions, positionId, cardType, cardKey) {
    const key = cardType === 'staffCards' ? 'uid' : cardType === 'classCards' ? 'classId' : cardType === 'gradeCards' ? 'gradeId' : 'subjectId';
    return positions.map(p => {
      if (p.id !== positionId) return p;
      return { ...p, [cardType]: (p[cardType] || []).filter(c => c[key] !== cardKey) };
    });
  },

  getDescendantIds,
  getAllPositions,
};

export default orgChartService;
